import React from 'react';
import FilterRow from './FilterRow';
import ReachCounter from './ReachCounter';
import SaveSegmentBar from './SaveSegmentBar';
import Icon from '../ui/Icon';
import EventBus from '../../utils/EventBus';
import { segmentService } from '../../services/segmentService';
import { api } from '../../utils/apiClient';
import { filtersToConditions, conditionsToFilters, toMatchType } from '../../utils/segmentConditions';
import styles from './SegmentBuilder.module.css';

const FILTER_BUTTONS = [
  { type: 'event',          ico: 'bolt',          label: 'Event' },
  { type: 'user-property',  ico: 'person',        label: 'Profile' },
  { type: 'entity-status',  ico: 'flag',          label: 'Status' },
  { type: 'earning',        ico: 'payments',      label: 'Earning' },
  { type: 'campaign',       ico: 'notifications', label: 'Campaign' },
];

class SegmentBuilder extends React.Component {
  constructor(props) {
    super(props);
    this._nextId = 1;
    this._reachTimer = null;

    const { initialSegment } = props;
    this.state = initialSegment
      ? {
          mode: 'filter',
          segments: [],
          selectedSegId: null,
          filters: conditionsToFilters(initialSegment.conditions, () => this._nextId++),
          op: initialSegment.match_type === 'ANY' ? 'OR' : 'AND',
          reach: initialSegment.user_count || 0,
          totalAudience: 1,
          reachLoading: false,
          editingId: initialSegment.id,
          editingName: initialSegment.name,
        }
      : {
          mode: 'saved',
          segments: [],
          selectedSegId: null,
          filters: [],
          op: 'AND',
          reach: 0,
          totalAudience: 1,
          reachLoading: false,
          editingId: null,
          editingName: '',
        };

    this.addFilter    = this.addFilter.bind(this);
    this.removeFilter = this.removeFilter.bind(this);
    this.updateFilter = this.updateFilter.bind(this);
    this.handleSave   = this.handleSave.bind(this);
  }

  componentDidMount() {
    // Campaigns built with "Build Filter" (instead of picking a saved
    // segment) silently create a one-off segment named "Audience — <campaign
    // name>" so the backend has a real segment_id to target — see
    // CampaignWizard.jsx's _resolveSegmentId(). Those aren't segments anyone
    // deliberately named/saved, so they shouldn't clutter this picker.
    segmentService.getAll().then((segments) => this.setState({
      segments: segments.filter((s) => !s.name.startsWith('Audience — ') && !s.name.startsWith('Campaign audience — ')),
    }));
    api.get('/analytics/kpis').then((kpis) => this.setState({ totalAudience: kpis.mau.value || 1 }));
    if (this.state.editingId) this._refreshReach();
  }

  addFilter(type) {
    const id = this._nextId++;
    const base = { id, type, event: '', prop: '', val: '', op: '', timeWindowValue: '30', timeWindowUnit: 'days' };
    if (type === 'event') {
      base.did = true;
      base.countOp = 'at least';
      base.countVal = '1';
      base.propFilters = [];
    }
    this.setState((s) => ({ filters: [...s.filters, base] }), () => this._notify());
  }

  removeFilter(id) {
    this.setState((s) => ({ filters: s.filters.filter((f) => f.id !== id) }), () => this._notify());
  }

  updateFilter(id, patch) {
    this.setState((s) => ({
      filters: s.filters.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }), () => this._notify());
  }

  _notify() {
    const { onChange } = this.props;
    if (onChange) onChange(this._buildValue());
    this._refreshReach();
  }

  _refreshReach() {
    clearTimeout(this._reachTimer);
    this._reachTimer = setTimeout(() => {
      const { mode, filters, op, selectedSegId, segments } = this.state;

      if (mode === 'saved') {
        const seg = segments.find((s) => s.id === selectedSegId);
        if (!seg) { this.setState({ reach: 0 }); return; }
        this.setState({ reachLoading: true });
        segmentService.previewSaved(seg.id)
          .then((r) => this.setState({ reach: r.count, reachLoading: false }))
          .catch(() => this.setState({ reachLoading: false }));
        return;
      }

      const conditions = filtersToConditions(filters);
      if (!conditions.length) { this.setState({ reach: 0 }); return; }
      this.setState({ reachLoading: true });
      segmentService.previewConditions(conditions, toMatchType(op))
        .then((r) => this.setState({ reach: r.count, reachLoading: false }))
        .catch(() => this.setState({ reachLoading: false }));
    }, 400);
  }

  _buildValue() {
    const { mode, filters, op, selectedSegId, reach } = this.state;
    return { mode, filters, op, selectedSegId, reach };
  }

  handleSave(name) {
    const { filters, op, editingId } = this.state;
    const conditions = filtersToConditions(filters);
    const payload = { name, conditions, match_type: toMatchType(op) };
    const request = editingId ? segmentService.update(editingId, payload) : segmentService.create(payload);

    request
      .then((segment) => {
        EventBus.emit('toast', { type: 'success', message: 'Segment "' + name + '" ' + (editingId ? 'updated.' : 'saved.') });
        this.setState((s) => ({
          segments: editingId
            ? s.segments.map((seg) => (seg.id === segment.id ? segment : seg))
            : [segment, ...s.segments],
        }));
        if (this.props.onSaved) this.props.onSaved(segment);
      })
      .catch((err) => EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to save segment.' }));
  }

  render() {
    const { mode, segments, filters, op, selectedSegId, reach, totalAudience, reachLoading } = this.state;
    const pct = Math.round((reach / totalAudience) * 100);

    return (
      <div className={styles.wrap}>
        {/* Mode selector */}
        <div className={styles.modeWrap}>
          <div
            className={[styles.modeBtn, mode === 'saved' ? styles.on : ''].join(' ')}
            onClick={() => this.setState({ mode: 'saved' }, () => this._notify())}
          >
            <div className={[styles.modeIco, mode === 'saved' ? styles.modeIcoOn : ''].join(' ')}><Icon name="bookmarks" size={16} /></div>
            <div className={styles.modeInfo}>
              <div className={styles.modeName}>Saved Segment</div>
              <div className={styles.modeDesc}>Pick from existing segments</div>
            </div>
            <div className={[styles.radio, mode === 'saved' ? styles.radioOn : ''].join(' ')} />
          </div>
          <div
            className={[styles.modeBtn, mode === 'filter' ? styles.on : ''].join(' ')}
            onClick={() => this.setState({ mode: 'filter' }, () => this._notify())}
          >
            <div className={[styles.modeIco, mode === 'filter' ? styles.modeIcoOn : ''].join(' ')}><Icon name="tune" size={16} /></div>
            <div className={styles.modeInfo}>
              <div className={styles.modeName}>Build Filter</div>
              <div className={styles.modeDesc}>Define custom conditions</div>
            </div>
            <div className={[styles.radio, mode === 'filter' ? styles.radioOn : ''].join(' ')} />
          </div>
        </div>

        {/* Saved segment list */}
        {mode === 'saved' && (
          <div className={styles.segList}>
            {segments.length === 0 && (
              <div className={styles.emptyFilters}>No saved segments yet — build one below.</div>
            )}
            {segments.map((seg) => (
              <div
                key={seg.id}
                className={[styles.segItem, selectedSegId === seg.id ? styles.segSelected : ''].join(' ')}
                onClick={() => this.setState({ selectedSegId: seg.id }, () => this._notify())}
              >
                <div className={styles.segDot} />
                <div className={styles.segInfo}>
                  <div className={styles.segName}>{seg.name}</div>
                  <div className={styles.segDesc}>{seg.description || (seg.conditions?.length || 0) + ' condition(s)'}</div>
                </div>
                <div className={styles.segCnt}>{seg.user_count || 0}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter builder */}
        {mode === 'filter' && (
          <div className={styles.fbShell}>
            <div className={styles.logicBar}>
              <span className={styles.logicTxt}>Users who match</span>
              <div className={styles.logicGrp}>
                <button
                  className={[styles.logicBtn, op === 'AND' ? styles.logicOn : ''].join(' ')}
                  onClick={() => this.setState({ op: 'AND' }, () => this._notify())}
                >ALL</button>
                <button
                  className={[styles.logicBtn, op === 'OR' ? styles.logicOn : ''].join(' ')}
                  onClick={() => this.setState({ op: 'OR' }, () => this._notify())}
                >ANY</button>
              </div>
              <span className={styles.logicTxt}>of these conditions</span>
            </div>

            <div className={styles.rowsWrap}>
              {filters.length === 0 && (
                <div className={styles.emptyFilters}>
                  Add conditions below to filter your audience
                </div>
              )}
              {filters.map((f, i) => (
                <FilterRow
                  key={f.id}
                  filter={f}
                  showConnector={i > 0}
                  op={op}
                  onOpChange={(newOp) => this.setState({ op: newOp }, () => this._notify())}
                  onRemove={this.removeFilter}
                  onUpdate={this.updateFilter}
                />
              ))}
            </div>

            <div className={styles.addStrip}>
              <span className={styles.addLbl}>ADD</span>
              {FILTER_BUTTONS.map((fb) => (
                <button
                  key={fb.type}
                  className={styles.addBtn}
                  onClick={() => this.addFilter(fb.type)}
                >
                  <Icon name={fb.ico} size={14} />
                  {fb.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <ReachCounter reach={reach} pct={pct} loading={reachLoading} />

        {mode === 'filter' && filters.length > 0 && (
          <SaveSegmentBar
            onSave={this.handleSave}
            isEditing={!!this.state.editingId}
            initialName={this.state.editingName}
            alwaysShowSave={this.props.standalone}
          />
        )}
      </div>
    );
  }
}

export default SegmentBuilder;
